import { Button, FileUploader, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import React, { useState } from 'react';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { GiftCardCategoryType } from 'utils/types';
import { ReactComponent as ArrowBackIcon } from 'assets/icons/arrow-back.svg';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import './style.scss';
import { useNavigate } from 'react-router-dom';
import { DeleteIcon } from 'cokitchen-icons';
import _ from 'lodash';

type EditProps = {
  data: GiftCardCategoryType | undefined;
  backLink: () => void;
};

type EditFormDataType = {
  name: string;
  images: {
    id: string | null;
    image_url: string | null;
  }[];
};

const EditCategoryDetails = ({ data, backLink }: EditProps) => {
  const navigate = useNavigate();
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;
  const [loading, setLoading] = useState(false);

  const getDefaultImages = () => {
    const imagesUploaded =
      data?.images.map((itm) => ({
        id: itm.id,
        image_url: itm.image_url
      })) ?? [];
    const imagesNotUploadedCount = 6 - imagesUploaded.length;
    const imagesNotUploaded = Array(imagesNotUploadedCount).fill({
      id: null,
      image_url: null
    });
    return imagesUploaded.concat(imagesNotUploaded);
  };

  const [editFormData, setEditFormData] = useState<EditFormDataType>({
    name: data?.name ?? '',
    images: getDefaultImages()
  });

  const handleUpdateImage = (url: string | null, index: number) => {
    const images = [...editFormData.images];
    const image = { ...images[index] };
    image.image_url = url;
    images[index] = image;
    setEditFormData({ ...editFormData, images });
  };

  const updateCategoryDetails = async () => {
    const imagesToEdit = editFormData.images.filter((item) => item.id && item.image_url);
    const imagesToCreate = editFormData.images.filter((item) => !item.id && item.image_url);
    const payload = {
      name: editFormData.name,
      images: imagesToEdit
    };
    if (imagesToCreate.length) {
      handleCreateImages(imagesToCreate.map((item) => item.image_url as string));
    }
    try {
      setLoading(true);
      await apiService.put(`vouchers/voucher-categories/${data?.id}`, {
        data: payload,
        isInternal: true,
        baseURL: process.env.REACT_APP_ORDER_API
      });
      toast(<ToastMessage type='success' message='Changes have been saved' />);
      navigate(ROUTE_KEYS.GIFTCARD_CATEGORIES);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    } finally {
      setLoading(false);
    }
  };

  const isChangesMade = !_.isEqual(editFormData, {
    name: data?.name ?? '',
    images: getDefaultImages()
  });

  const resetData = () => {
    setEditFormData({
      name: data?.name ?? '',
      images: getDefaultImages()
    });
  };

  const handleDeleteImage = async (id: string | null, index: number) => {
    const images = [...editFormData.images];
    const image = { ...images[index] };

    if (!id) {
      image.image_url = null;
      images[index] = image;
      setEditFormData({ ...editFormData, images });
      return;
    }
    try {
      await apiService.delete(`vouchers/voucher-images/${id}`, {
        isInternal: true,
        baseURL: process.env.REACT_APP_ORDER_API
      });
      image.image_url = null;
      image.id = null;
      images[index] = image;
      setEditFormData({ ...editFormData, images });
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    }
  };

  const handleCreateImages = async (image_urls: string[]) => {
    const payload = {
      image_urls,
      voucher_category_id: data?.id
    };
    try {
      await apiService.post(`vouchers/voucher-images`, {
        data: payload,
        isInternal: true,
        baseURL: process.env.REACT_APP_ORDER_API
      });
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    }
  };

  return (
    <div>
      <Input
        type='text'
        label='Category name'
        value={editFormData?.name}
        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
      />
      <div className='border border-[#EAEBEB] rounded-xl mt-6 '>
        <div className='px-6 py-[18px]'>
          <p className=' text-[#1A1A1A] text-sm  font-medium '>Category Recommended Images</p>
          <p className='text-[#595959] text-xs'>Maximum of 6 images</p>
        </div>
        <hr className='text-[#EAEBEB]' />
        <div className='p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
          {editFormData?.images?.map((item, index) => (
            <div key={`${item.image_url}${index}`} className='relative group '>
              <FileUploader
                token={token}
                fileUrls={item.image_url ? [item.image_url] : []}
                setFileUrls={(urls) => handleUpdateImage(urls[0], index)}
                className='h-[160px]'
              />

              {item.image_url ? (
                <div
                  onClick={() => handleDeleteImage(item.id, index)}
                  className='absolute top-[14px] right-5 cursor-pointer opacity-0 group-hover:opacity-100 '>
                  <DeleteIcon className='text-[#fff]' />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className='flex justify-between mt-6'>
        <div
          onClick={backLink}
          className='bg-[#F5F4F4] w-[46px] h-[46px] rounded-full flex justify-center items-center cursor-pointer'>
          <ArrowBackIcon className='text-gray-3 ' />
        </div>

        <div className='flex gap-3 items-center'>
          {isChangesMade ? (
            <p onClick={resetData} className='text-[#E60A2B] text-sm font-medium cursor-pointer'>
              Undo changes
            </p>
          ) : null}
          <Button
            color='primary'
            variant='block'
            className='h-[46px] w-[151px] '
            loading={loading}
            disabled={!isChangesMade}
            onClick={updateCategoryDetails}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditCategoryDetails;
